(() => {
  const doc = document;
  const root = doc.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = window.matchMedia('(pointer:fine)').matches;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const setScroll = () => {
    const max = Math.max(1, doc.documentElement.scrollHeight - window.innerHeight);
    root.style.setProperty('--rr-scroll', Math.min(1, Math.max(0, window.scrollY / max)));
  };
  setScroll();
  window.addEventListener('scroll', setScroll, { passive: true });
  window.addEventListener('resize', setScroll, { passive: true });

  if (!reduced && !designMode && fine) {
    const stages = [...doc.querySelectorAll('[data-rr-parallax]')];
    stages.forEach(stage => {
      if (stage.dataset.rrParallaxInit === 'true') return;
      stage.dataset.rrParallaxInit = 'true';
      stage.addEventListener('pointermove', e => {
        const r = stage.getBoundingClientRect();
        const x = ((e.clientX - r.left) / Math.max(1, r.width) - .5);
        const y = ((e.clientY - r.top) / Math.max(1, r.height) - .5);
        stage.style.setProperty('--rr-mx', `${x * 18}px`);
        stage.style.setProperty('--rr-my', `${y * 14}px`);
      });
      stage.addEventListener('pointerleave', () => {
        stage.style.setProperty('--rr-mx', '0px');
        stage.style.setProperty('--rr-my', '0px');
      });
    });
  }

  const closeNav = scope => {
    scope.querySelectorAll?.('.rr-mobile-nav a,.rr-navdrop a').forEach(link => {
      if (link.dataset.rrNavInit === 'true') return;
      link.dataset.rrNavInit = 'true';
      link.addEventListener('click', () => link.closest('details')?.removeAttribute('open'));
    });
  };
  closeNav(doc);
  doc.addEventListener('shopify:section:load', e => closeNav(e.target));

  const clampQuantity = (input, min = 1) => {
    const parsed = parseInt(input.value, 10);
    const value = Number.isFinite(parsed) ? Math.max(min, parsed) : min;
    input.value = String(value);
    return value;
  };

  const enhanceQuantity = (input, options = {}) => {
    if (!input || input.dataset.csQtyEnhanced === 'true') return;
    input.dataset.csQtyEnhanced = 'true';

    const min = Math.max(1, parseInt(options.min || input.min || '1', 10) || 1);
    input.min = String(min);
    input.inputMode = 'numeric';

    const wrapper = doc.createElement('div');
    wrapper.className = `cs-qty-stepper ${options.cart ? 'cs-qty-stepper--cart' : 'cs-qty-stepper--product'}`;

    const minus = doc.createElement('button');
    minus.type = 'button';
    minus.className = 'cs-qty-stepper__button cs-qty-stepper__button--minus';
    minus.setAttribute('aria-label', options.minusLabel || 'Decrease quantity');
    minus.textContent = '−';

    const plus = doc.createElement('button');
    plus.type = 'button';
    plus.className = 'cs-qty-stepper__button cs-qty-stepper__button--plus';
    plus.setAttribute('aria-label', options.plusLabel || 'Increase quantity');
    plus.textContent = '+';

    input.parentNode.insertBefore(wrapper, input);
    wrapper.appendChild(minus);
    wrapper.appendChild(input);
    wrapper.appendChild(plus);

    const sync = () => {
      const value = clampQuantity(input, min);
      minus.disabled = value <= min;
      minus.setAttribute('aria-disabled', value <= min ? 'true' : 'false');
      return value;
    };

    const notify = () => {
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    minus.addEventListener('click', () => {
      const value = sync();
      input.value = String(Math.max(min, value - 1));
      sync();
      notify();
    });

    plus.addEventListener('click', () => {
      const value = sync();
      input.value = String(value + 1);
      sync();
      notify();
    });

    input.addEventListener('change', sync);
    sync();
  };

  const repairPurchaseButtons = scope => {
    scope.querySelectorAll?.('[data-product-root]').forEach(productRoot => {
      const add = productRoot.querySelector('[data-add-button]');
      if (add) {
        const label = (add.dataset.label || 'ADD TO CART').trim() || 'ADD TO CART';
        if (!add.textContent.trim()) add.textContent = label;
        add.style.setProperty('color', '#FFFFFF', 'important');
        add.style.setProperty('-webkit-text-fill-color', '#FFFFFF', 'important');
        const addStyle = window.getComputedStyle(add);
        if (addStyle.backgroundColor === 'rgba(0, 0, 0, 0)' && addStyle.backgroundImage === 'none') {
          add.style.setProperty('background', '#8E0F1B', 'important');
        }
      }

      productRoot.querySelectorAll('[data-buy-now]').forEach(buy => {
        if (!buy.textContent.trim()) buy.textContent = 'BUY IT NOW!';
        buy.style.setProperty('color', '#FFFFFF', 'important');
        buy.style.setProperty('-webkit-text-fill-color', '#FFFFFF', 'important');
        const buyStyle = window.getComputedStyle(buy);
        if (buyStyle.backgroundColor === 'rgba(0, 0, 0, 0)' && buyStyle.backgroundImage === 'none') {
          buy.style.setProperty('background', 'linear-gradient(110deg,#1539C9,#2F5BFF)', 'important');
        }
      });

      const productQty = productRoot.querySelector('.cs2-product-form input[name="quantity"]');
      enhanceQuantity(productQty, {
        min: 1,
        minusLabel: 'Decrease product quantity',
        plusLabel: 'Increase product quantity'
      });
    });
  };

  const enhanceCart = scope => {
    scope.querySelectorAll?.('.cs-cart-row').forEach(row => {
      const title = row.querySelector('h3')?.textContent?.trim() || 'item';
      const qty = row.querySelector('input[name="updates[]"]');
      if (qty && qty.dataset.csCartAutoUpdate !== 'true') {
        qty.dataset.csCartAutoUpdate = 'true';
        enhanceQuantity(qty, {
          cart: true,
          min: 1,
          minusLabel: `Decrease ${title} quantity`,
          plusLabel: `Increase ${title} quantity`
        });

        const form = qty.closest('form');
        const updateButton = form?.querySelector('button[name="update"]');
        let timer;
        const scheduleUpdate = () => {
          if (!form || !updateButton) return;
          clearTimeout(timer);
          timer = setTimeout(() => {
            if (typeof form.requestSubmit === 'function') form.requestSubmit(updateButton);
            else updateButton.click();
          }, 450);
        };
        qty.addEventListener('change', scheduleUpdate);
      }

      row.querySelectorAll('a').forEach(link => {
        const href = link.getAttribute('href') || '';
        const isRemove = link.textContent.trim().toLowerCase() === 'remove' || href.includes('quantity=0');
        if (!isRemove) return;
        link.classList.add('cs-cart-remove');
        link.textContent = 'Remove item';
        link.setAttribute('aria-label', `Remove ${title} from cart`);
      });
    });
  };

  const initCommerceFixes = scope => {
    repairPurchaseButtons(scope || doc);
    enhanceCart(scope || doc);
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', () => initCommerceFixes(doc), { once: true });
  } else {
    initCommerceFixes(doc);
  }

  doc.addEventListener('shopify:section:load', event => initCommerceFixes(event.target));
})();

/* Keep the header cart badge synced after normal and AJAX cart changes. */
(() => {
  const doc = document;

  const paintCartBadge = count => {
    const quantity = Math.max(0, Number(count) || 0);
    doc.querySelectorAll('.rr-cart b').forEach(badge => {
      badge.textContent = quantity > 99 ? '99+' : String(quantity);
      badge.hidden = quantity === 0;
      const link = badge.closest('.rr-cart');
      if (link) link.setAttribute('aria-label', `Cart, ${quantity} ${quantity === 1 ? 'item' : 'items'}`);
    });
  };

  const syncCartBadge = async () => {
    try {
      const rootUrl = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${rootUrl}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return;
      const cart = await response.json();
      paintCartBadge(cart.item_count);
    } catch (_) {}
  };

  const scheduleCartBadgeSync = () => {
    window.setTimeout(syncCartBadge, 450);
    window.setTimeout(syncCartBadge, 1100);
  };

  const initCartBadge = () => {
    syncCartBadge();
    doc.addEventListener('submit', event => {
      const form = event.target.closest?.('form[action*="/cart/add"]');
      if (form) scheduleCartBadgeSync();
    }, true);

    ['cart:updated', 'cart:refresh', 'cart:update', 'cart:change'].forEach(name => {
      doc.addEventListener(name, scheduleCartBadgeSync);
    });
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', initCartBadge, { once: true });
  } else {
    initCartBadge();
  }

  window.addEventListener('pageshow', syncCartBadge);
})();
